/**
 * Audit Log List Component
 *
 * Main component displaying the list of audit logs with filters.
 * @see US-19a.28
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton, EmptyState, Pagination } from '@/components/ui';
import { ScrollText } from 'lucide-react';
import type { DateRange } from '@/components/ui';
import type { AuditLogFilters as AuditLogFiltersType, AuditAction, AuditLogListItem } from '@bstock/shared';
import { formatDateParam } from '@/components/ui';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { AuditLogFilters } from './AuditLogFilters';
import { AuditLogItem } from './AuditLogItem';
import { AuditLogDetailDrawer } from './AuditLogDetail';

const DEFAULT_LIMIT = 20;

/**
 * Get initial date range (last 7 days)
 */
function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date();
  start.setDate(now.getDate() - 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Parse URL params into filters (deep linking)
 */
function parseUrlParams(searchParams: URLSearchParams): {
  entityType: string;
  action: string;
  actorId: string;
  search: string;
  dateRange: DateRange;
  page: number;
} {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const defaultRange = getInitialDateRange();

  return {
    entityType: searchParams.get('entityType') ?? '',
    action: searchParams.get('action') ?? '',
    actorId: searchParams.get('actorId') ?? '',
    search: searchParams.get('search') ?? '',
    dateRange: {
      start: startDate ? new Date(startDate) : defaultRange.start,
      end: endDate ? new Date(endDate) : defaultRange.end,
    },
    page: parseInt(searchParams.get('page') ?? '1', 10),
  };
}

export function AuditLogList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Filters state from URL
  const [entityTypeFilter, setEntityTypeFilter] = useState(urlParams.entityType);
  const [actionFilter, setActionFilter] = useState(urlParams.action);
  const [actorIdFilter, setActorIdFilter] = useState(urlParams.actorId);
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [searchQuery, setSearchQuery] = useState(urlParams.search);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  // Detail drawer state
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Query params for API
  const queryParams: AuditLogFiltersType = useMemo(
    () => ({
      page,
      limit,
      entityType: entityTypeFilter || undefined,
      action: actionFilter as AuditAction | undefined,
      actorId: actorIdFilter || undefined,
      search: searchQuery || undefined,
      dateFrom: formatDateParam(dateRange.start) ?? undefined,
      dateTo: formatDateParam(dateRange.end) ?? undefined,
    }),
    [page, limit, entityTypeFilter, actionFilter, actorIdFilter, searchQuery, dateRange]
  );

  // Fetch audit logs
  const { data, isLoading } = useAuditLogs(queryParams);

  const logs = data?.items ?? [];
  const pagination = data?.pagination;

  // Handlers
  const handleEntityTypeChange = useCallback(
    (value: string) => {
      setEntityTypeFilter(value);
      setPage(1);
      updateUrl({ entityType: value || null, page: null });
    },
    [updateUrl]
  );

  const handleActionChange = useCallback(
    (value: string) => {
      setActionFilter(value);
      setPage(1);
      updateUrl({ action: value || null, page: null });
    },
    [updateUrl]
  );

  const handleActorIdChange = useCallback(
    (value: string) => {
      setActorIdFilter(value);
      setPage(1);
      updateUrl({ actorId: value || null, page: null });
    },
    [updateUrl]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setPage(1);
      updateUrl({ search: value || null, page: null });
    },
    [updateUrl]
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      setPage(1);
      updateUrl({
        startDate: formatDateParam(range.start),
        endDate: formatDateParam(range.end),
        page: null,
      });
    },
    [updateUrl]
  );

  const handleReset = useCallback(() => {
    const defaultRange = getInitialDateRange();
    setEntityTypeFilter('');
    setActionFilter('');
    setActorIdFilter('');
    setSearchQuery('');
    setDateRange(defaultRange);
    setPage(1);
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateUrl({ page: String(newPage) });
    },
    [updateUrl]
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      setLimit(newLimit);
      setPage(1);
      updateUrl({ page: null });
    },
    [updateUrl]
  );

  const handleLogClick = useCallback((log: AuditLogListItem) => {
    setSelectedLogId(log.id);
    setIsDetailOpen(true);
  }, []);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <AuditLogFilters
        entityType={entityTypeFilter}
        action={actionFilter}
        actorId={actorIdFilter}
        searchQuery={searchQuery}
        dateRange={dateRange}
        onEntityTypeChange={handleEntityTypeChange}
        onActionChange={handleActionChange}
        onActorIdChange={handleActorIdChange}
        onSearchChange={handleSearchChange}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
      />

      {/* List */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title="Aucun log trouve"
            description="Aucune activite ne correspond a vos criteres de recherche"
          />
        ) : (
          <div>
            {logs.map((log) => (
              <AuditLogItem
                key={log.id}
                log={log}
                onClick={() => handleLogClick(log)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={limit}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleLimitChange}
          />
        </div>
      )}

      {/* Detail drawer */}
      <AuditLogDetailDrawer
        logId={selectedLogId}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
      />
    </div>
  );
}
